import React from 'react';
import { Box, Typography, Button, Card } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useTranslation } from 'react-i18next';
import { getBetaAppUrl } from '../../../utils/betaApp';

interface PurchaseActionsBoxProps {
    isPurchased?: boolean;
}

const PurchaseActionsBox: React.FC<PurchaseActionsBoxProps> = ({
    isPurchased = false
}) => {
    const { t } = useTranslation();

    if (isPurchased) {
        return (
            <Card data-purchase-box sx={{}}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
                    <Typography variant="h6" color="success.dark">
                        {t('taxReports.planPurchased', 'Plan erworben!')}
                    </Typography>
                </Box>
            </Card>
        );
    }

    const handlePurchase = () => {
        window.location.href = `${getBetaAppUrl()}/pro/purchase.php`;
    };

    return (
        <Card data-purchase-box sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
                <ShoppingCartIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                
                <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
                    {t('taxReports.unlockReport', 'Steuerbericht freischalten')}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Bitte kaufen Sie einen Plan auf unserer Website, um auf Ihre Steuerberichte zuzugreifen.
                </Typography>
                
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<ShoppingCartIcon />}
                    onClick={handlePurchase}
                    sx={{ mb: 2 }}
                >
                    Plan kaufen
                </Button>
                
                <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 1.5,
                    mt: 3,
                    pt: 3,
                    borderTop: 1,
                    borderColor: 'divider'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mt: 0.1 }} />
                        <Typography variant="body2">
                            {t('taxReports.taxRelevantSums', 'Steuerrelevante Summen im Überblick')}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mt: 0.1 }} />
                        <Typography variant="body2">
                            {t('taxReports.completeTransactionOverview', 'Komplette Transaktionsübersicht')}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mt: 0.1 }} />
                        <Typography variant="body2">
                            {t('taxReports.automaticProfitLossCalc', 'Automatische Gewinn- & Verlustberechnung')}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mt: 0.1 }} />
                        <Typography variant="body2">
                            {t('taxReports.pdfExcelDownload', 'PDF- & Excel-Download')}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Card>
    );
};

export default PurchaseActionsBox;